'use client';
import React__default, { useEffect } from 'react';
import { searchInputPropTypes, SearchInput } from '../search-input/search-input.component.js';
import { ComboboxInput } from './combobox-input.component.js';
import { useSwanComboboxContext } from './combobox.context.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { useSwanPopover } from '../popover/popover.context.js';
import { RenderComp } from '../core/render-comp.component.js';
import { assignRefs } from '../../../core/utilities/ref.utils.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
/**
 * @subcomponent Combobox
 */ const ComboboxSearchInput = renderWithRef('ComboboxSearchInput', searchInputPropTypes, (props, ref)=>{
    const { inputRef, triggerRef, setInputValue } = useSwanComboboxContext();
    const { overlayState } = useSwanPopover();
    const combinedRef = assignRefs(ref, inputRef);
    const getSearchInputProps = (p)=>{
        return _object_spread_props(_object_spread({}, p), {
            onClear: (e)=>{
                var _props_onClear;
                (_props_onClear = props.onClear) === null || _props_onClear === void 0 ? void 0 : _props_onClear.call(props, e);
                setInputValue('');
            }
        });
    };
    /**
   * Workaround for issue where react-aria is stealing the enter keypress event and stopping the form from submitting when allowsCustomValue is set
   * https://github.com/adobe/react-spectrum/issues/6228
   */ useEffect(()=>{
        const inputEl = inputRef.current;
        const cb = (e)=>{
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- react-aria types are not correct
            const focusedKey = overlayState.selectionManager.focusedKey;
            // if the enter key is pressed, and no listbox key is focused, submit the form by clicking the submit button in the search input
            if (e.key === 'Enter' && !focusedKey) {
                var _triggerRef_current;
                const button = (_triggerRef_current = triggerRef.current) === null || _triggerRef_current === void 0 ? void 0 : _triggerRef_current.querySelector('button[type="submit"]');
                button === null || button === void 0 ? void 0 : button.click();
            }
        };
        inputEl === null || inputEl === void 0 ? void 0 : inputEl.addEventListener('keydown', cb);
        return ()=>inputEl === null || inputEl === void 0 ? void 0 : inputEl.removeEventListener('keydown', cb);
    }, [
        inputRef,
        triggerRef,
        overlayState
    ]);
    return /*#__PURE__*/ React__default.createElement(RenderComp, {
        root: "div",
        forwardedRef: triggerRef,
        classNames: [
            'swan-combobox-search-input-container'
        ],
        props: {}
    }, /*#__PURE__*/ React__default.createElement(ComboboxInput, _object_spread_props(_object_spread({}, props), {
        ref: combinedRef,
        render: (p, r)=>/*#__PURE__*/ React__default.createElement(RenderComp, {
                root: SearchInput,
                forwardedRef: r,
                props: getSearchInputProps(p)
            })
    })));
});

export { ComboboxSearchInput };
